/**
 * Générateur — Modéliser des problèmes relevant des opérations à trous par des
 * équations du type ax = c ou x + b = c.
 * Attendu ID : 942 | Niveau : 5ème | Domaine : Calcul littéral (cluster 8, O44)
 *
 * Source : doc prof « TANOED EXERCISEUR nombre et calcul », thème Calcul
 * littéral, cluster 8-9 (Créé n°29a « Choisir la bonne équation »,
 * explicitement « pour compléter O44 » — le pont opérations à trous ↔
 * équations est porté par gen_941).
 *
 * Plan de cartes (validé 2026-07-12) :
 *
 *   Carte « choisir » — Créé n°29a : situation → équation. Familles du doc :
 *   rectangle (largeur k, aire A → kx = A, aire décimale x,5 comme le doc
 *   19,5), dépense (x − d = r), « le tiers/quart vaut v » (x/3 = v, l'exemple
 *   du cadrage), gain (x + b = c), k objets identiques (kx = c).
 *   qcm_unique (distracteurs : opération inversée, nombres échangés) +
 *   relier (4 situations ↔ 4 équations — familles ET nombres tous distincts
 *   par construction pour interdire les croisements).
 *
 *   Carte « situation » — sens inverse : « quelle situation se traduit par
 *   3x = 19,5 ? » — les 4 choix utilisent les MÊMES nombres dans des
 *   structures différentes (x − 3 = 19,5, x + 3 = 19,5, x/3 = 19,5) →
 *   la structure seule décide, unicité par construction.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeRelier,
} from '../../../utils';

const ATTENDU_ID = 942;

const L_CHOISIR = 'Choisir l\'équation qui modélise la situation';
const L_SITUATION = 'Retrouver la situation derrière l\'équation';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const MOINS = '−';   // U+2212 (affichage uniquement)

// décimal français depuis des dixièmes entiers
const frT = (t) => (t % 10 === 0 ? String(t / 10) : `${Math.floor(t / 10)},${t % 10}`);

// ═════════ Familles de situations (la structure décide de l'équation) ═════════

/**
 * Chaque famille produit { texte, eq, eqFausses } — les eqFausses sont les
 * MÊMES nombres dans une structure inversée/échangée (misconceptions).
 */
const FAMILLES_SITUATIONS = [
    {
        id: 'rectangle',
        tirer: () => {
            const k = randInt(2, 6);
            const x10 = randInt(3, 25) * 5 + (randomChoice([0, 5]));   // longueur en dixièmes
            const A10k = k * x10;                                      // aire en dixièmes
            const A = frT(A10k);
            return {
                texte: `Un rectangle de largeur ${k} cm a une aire de ${A} cm². On cherche sa longueur x.`,
                eq: `${k}x = ${A}`,
                eqFausses: [`x + ${k} = ${A}`, `x ÷ ${k} = ${A}`, `x ${MOINS} ${k} = ${A}`],
            };
        },
    },
    {
        id: 'depense',
        tirer: () => {
            const d10 = randInt(2, 20) * 5;
            const r10 = randInt(2, 30) * 5;
            const d = frT(d10), r = frT(r10);
            return {
                texte: `Après avoir dépensé ${d} €, il me reste ${r} €. On cherche la somme de départ x.`,
                eq: `x ${MOINS} ${d} = ${r}`,
                eqFausses: [`x + ${d} = ${r}`, `${d}x = ${r}`, `${d} ${MOINS} x = ${r}`],
            };
        },
    },
    {
        id: 'fraction',
        tirer: () => {
            const den = randomChoice([3, 4]);
            const nom = den === 3 ? 'tiers' : 'quart';
            const v = randInt(1, 12);
            return {
                texte: `Le ${nom} d'un nombre x vaut ${v}.`,
                // Quotient en fraction verticale KaTeX (audit fractions en texte, 2026-09-06).
                eq: `$\\dfrac{x}{${den}} = ${v}$`,
                eqFausses: [`${den}x = ${v}`, `x ${MOINS} ${den} = ${v}`, `x + ${den} = ${v}`],
            };
        },
    },
    {
        id: 'gain',
        tirer: () => {
            const b = randInt(2, 20);
            const c = b + randInt(5, 40);
            return {
                texte: `J'ai gagné ${b} € aujourd'hui : j'ai maintenant ${c} €. On cherche la somme de départ x.`,
                eq: `x + ${b} = ${c}`,
                eqFausses: [`x ${MOINS} ${b} = ${c}`, `${b}x = ${c}`, `x + ${c} = ${b}`],
            };
        },
    },
    {
        id: 'objets',
        tirer: () => {
            const k = randInt(2, 8);
            const x10 = randInt(10, 60) * 5;
            const c = frT(k * x10);
            return {
                texte: `${k} places de cinéma identiques coûtent ${c} € en tout. On cherche le prix x d'une place.`,
                eq: `${k}x = ${c}`,
                eqFausses: [`x + ${k} = ${c}`, `x ÷ ${k} = ${c}`, `${k} + x = ${c}`],
            };
        },
    },
];

// ═════════ Carte 1 — Choisir l'équation (Créé n°29a) ═════════

const genChoisirQcm = () => {
    const famille = randomChoice(FAMILLES_SITUATIONS);
    const t = famille.tirer();
    const texte = `<p><b>${t.texte}</b> Quelle équation modélise la situation ?</p>`;
    const distincts = [...new Set(t.eqFausses)].filter(e => e !== t.eq).slice(0, 3);
    const payload = makeQcm([t.eq, ...distincts], t.eq, texte, opts(L_CHOISIR));
    payload._meta.params = { famille: famille.id, eq: t.eq };
    return payload;
};

const genChoisirRelier = () => {
    // 4 familles distinctes → structures distinctes ; on garantit AUSSI des
    // équations rendues distinctes (les nombres varient par famille)
    for (let essai = 0; essai < 30; essai++) {
        const familles = shuffle(FAMILLES_SITUATIONS).slice(0, 4);
        const tirages = familles.map(f => f.tirer());
        const eqs = tirages.map(t => t.eq);
        if (new Set(eqs).size !== 4) continue;
        const pairs = tirages.map(t => ({ source: t.texte, target: t.eq }));
        const payload = makeRelier(pairs,
            '<p><b>Relie chaque situation à l\'équation qui la modélise</b> (x est l\'inconnue).</p>',
            opts(L_CHOISIR));
        payload._meta.params = { familles: familles.map(f => f.id) };
        return payload;
    }
    // repli déterministe (structures et nombres tous distincts, vérifiés à la main)
    const pairs = [
        { source: 'Un rectangle de largeur 3 cm a une aire de 19,5 cm². On cherche sa longueur x.', target: '3x = 19,5' },
        { source: 'Après avoir dépensé 4,5 €, il me reste 12 €. On cherche la somme de départ x.', target: `x ${MOINS} 4,5 = 12` },
        { source: 'Le tiers d\'un nombre x vaut 1.', target: '$\\dfrac{x}{3} = 1$' },
        { source: 'J\'ai gagné 8 € aujourd\'hui : j\'ai maintenant 20 €. On cherche la somme de départ x.', target: 'x + 8 = 20' },
    ];
    const payload = makeRelier(pairs,
        '<p><b>Relie chaque situation à l\'équation qui la modélise</b> (x est l\'inconnue).</p>',
        opts(L_CHOISIR));
    payload._meta.params = { familles: ['repli'] };
    return payload;
};

// ═════════ Carte 2 — Retrouver la situation (sens inverse) ═════════

// pour une équation donnée, les 4 situations candidates utilisent les MÊMES
// nombres dans des structures différentes — seule la structure décide
const genSituationQcm = () => {
    const structure = randomChoice(['produit', 'moins', 'plus']);
    const k = randInt(2, 6);
    const A10 = randInt(4, 40) * 5;
    const A = frT(A10);
    const situations = {
        produit: `Un rectangle de largeur ${k} cm a une aire de ${A} cm². On cherche sa longueur x.`,
        moins: `Après avoir dépensé ${k} €, il me reste ${A} €. On cherche la somme de départ x.`,
        plus: `J'ai gagné ${k} € aujourd'hui : j'ai maintenant ${A} €. On cherche la somme de départ x.`,
        fraction: `Un nombre x partagé en ${k} parts égales donne ${A} par part.`,
    };
    const eqs = {
        produit: `${k}x = ${A}`,
        moins: `x ${MOINS} ${k} = ${A}`,
        plus: `x + ${k} = ${A}`,
    };
    const texte = `<p><b>Quelle situation se traduit par l'équation ${eqs[structure]} ?</b></p>`;
    const correct = situations[structure];
    const candidats = Object.keys(situations).filter(s => s !== structure)
        .map(s => situations[s]);
    const payload = makeQcm([correct, ...candidats], correct, texte, opts(L_SITUATION));
    payload._meta.params = { structure, k, A };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    choisir_qcm_unique: genChoisirQcm,
    choisir_relier: genChoisirRelier,
    situation_qcm_unique: genSituationQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', relier: 'Relier',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'choisir', label: L_CHOISIR, formatsList: buildFormats('choisir') },
    { id: 'situation', label: L_SITUATION, formatsList: buildFormats('situation') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
